import { memo } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,212,255,0.05)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-bright)',
  clipPath: 'polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%)',
};

/**
 * The bot's UT Bot settings: these drive the server's signals, this panel's
 * list, alerts and auto-trade. Charts keep their own settings (chart header)
 * for their arrows, so trying values on a chart never changes what the bot
 * trades until you copy them here.
 */
export const BotConfigForm = memo(function BotConfigForm() {
  const bot          = useTerminalStore(s => s.settings.defaultConfig);
  const setBotConfig = useTerminalStore(s => s.setBotConfig);
  const panels       = useTerminalStore(s => s.activePanels);
  const focusedIdx   = useTerminalStore(s => s.focusedPanelIndex);
  const chart        = panels[focusedIdx] ?? panels[0];
  const chartDiffers = chart !== undefined &&
    (chart.config.keyValue !== bot.keyValue || chart.config.atrPeriod !== bot.atrPeriod);

  return (
    <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-1.5">
      <div className="font-mono text-[11px] mb-1" style={{ color: 'var(--color-muted)' }}>
        <span className="text-arc font-bold">BOT SETTINGS</span>
        <span className="block opacity-90">Used for these signals, alerts and auto-trade on every symbol and timeframe. Signals count only once their candle closes.</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] min-w-[72px] cursor-help underline decoration-dotted" style={{ color: 'var(--color-text)' }} title="Key Value: UT Bot sensitivity multiplier on ATR. Higher = wider trailing stop, fewer signals.">KEY VAL</span>
        <input
          type="number"
          min={0.1} max={5} step={0.1}
          value={bot.keyValue}
          onChange={e => setBotConfig({ keyValue: parseFloat(e.target.value) })}
          aria-label="Bot key value"
          className="flex-1 min-w-0 font-mono text-[11px] px-1.5 py-0.5 outline-none"
          style={inputStyle}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] min-w-[72px] cursor-help underline decoration-dotted" style={{ color: 'var(--color-text)' }} title="ATR Period: number of candles for the Average True Range (volatility) used by the trailing stop.">ATR PERIOD</span>
        <input
          type="number"
          min={2} max={50} step={1}
          value={bot.atrPeriod}
          onChange={e => setBotConfig({ atrPeriod: parseInt(e.target.value) })}
          aria-label="Bot ATR period"
          className="flex-1 min-w-0 font-mono text-[11px] px-1.5 py-0.5 outline-none"
          style={inputStyle}
        />
      </div>

      {chart && (
        <button
          onClick={() => setBotConfig({ keyValue: chart.config.keyValue, atrPeriod: chart.config.atrPeriod })}
          disabled={!chartDiffers}
          title="Use the selected chart's KEY VAL / ATR PERIOD for the bot"
          className="mt-1 font-mono text-[11px] border border-arc/40 text-arc px-2 py-1 hover:bg-arc/10 transition-colors tracking-wider disabled:opacity-40 disabled:cursor-default"
        >
          {chartDiffers
            ? `COPY FROM ${chart.symbol} ${chart.timeframe} CHART (K${chart.config.keyValue}·P${chart.config.atrPeriod})`
            : `${chart.symbol} ${chart.timeframe} CHART MATCHES BOT`}
        </button>
      )}

      <TuningNotes />
    </div>
  );
});

// Rules of thumb, not tested values: ATR already scales the stop to each
// timeframe's volatility, but short timeframes whipsaw more relative to their
// trends, so they usually want a wider stop (higher KEY VAL).
const STARTING_POINTS: Array<[timeframes: string, keyValue: number, atrPeriod: number, why: string]> = [
  ['1m – 5m',  2,   10, 'Noisy: a wider stop cuts whipsaw trades. Still many signals.'],
  ['15m – 1h', 1.5, 10, 'Middle ground between speed and noise.'],
  ['4h – 1D',  1,   10, "The script's original default. Try K 2 · P 14 for fewer flips."],
];

function TuningNotes() {
  return (
    <div className="mt-3 pt-2 font-mono text-[11px] flex flex-col gap-1.5" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text)' }}>
      <div className="text-arc font-bold tracking-wider">TUNING NOTES</div>
      <div><span className="text-bright">KEY VAL ↑</span> wider trailing stop: fewer, later signals and fewer whipsaws, but gives back more before exiting.</div>
      <div><span className="text-bright">ATR PERIOD ↑</span> smoother volatility: the stop reacts more slowly to sudden moves. 10–14 is typical.</div>

      <div className="mt-1 text-muted">STARTING POINTS (rules of thumb, not proven best)</div>
      {/* Stacked rather than a table: the panel is narrow in the default layout */}
      {STARTING_POINTS.map(([tf, kv, atr, why]) => (
        <div key={tf}>
          <span className="text-bright">{tf}</span>{'  '}
          <span className="text-accent whitespace-nowrap">K {kv} · P {atr}</span>
          <span className="block text-muted">{why}</span>
        </div>
      ))}

      <div className="text-muted">
        The bot uses one setting for every timeframe, so tune it for the timeframes auto-trade trades.
        Test on your own data with AUTO-TRADE → OPTIMIZER (APPLY BEST sets the bot). It ignores fees and
        scores on the same ~500 candles it tunes on, so prefer values that rank well across several coins
        over the single top result.
      </div>
    </div>
  );
}
